import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Star, TrendingUp, Zap, Award, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import AuthModal from '@/components/AuthModal';
import Navbar from '@/components/Navbar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { user } = useAuth();
  const { getItemCount } = useCart();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
    const starsContainer = document.querySelector('.stars');
    if (!starsContainer) return;
    
    for (let i = 0; i < 100; i++) {
      const star = document.createElement('div');
      star.className = 'star';
      star.style.left = `${Math.random() * 100}%`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.animationDelay = `${Math.random() * 3}s`;
      starsContainer.appendChild(star);
    }
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const allProducts = response.data.products;
      setProducts(allProducts);
      // Featured products - first 8
      setFeaturedProducts(allProducts.slice(0, 8));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="stars"></div>
      <Navbar />

      {/* Hero Section - Revamped */}
      <section className="relative z-10 pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 mb-6">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-medium text-purple-300">15 Unique Cosmic Designs Available</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              Wear The
              <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                Cosmic Revolution
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              AI-generated designs featuring cool cats conquering the cosmos. Premium 100% cotton t-shirts that are out of this world.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button 
                onClick={() => navigate('/shop')}
                className="btn-primary flex items-center gap-2 text-lg px-8 py-4"
                data-testid="hero-shop-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                Explore Collection
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="glass-card text-center py-4" data-testid="stat-designs">
                <div className="text-3xl font-bold text-purple-400 mb-1">15+</div>
                <div className="text-sm text-gray-400">Unique Designs</div>
              </div>
              <div className="glass-card text-center py-4" data-testid="stat-price">
                <div className="text-3xl font-bold text-blue-400 mb-1">₹999</div>
                <div className="text-sm text-gray-400">Starting Price</div>
              </div>
              <div className="glass-card text-center py-4" data-testid="stat-quality">
                <div className="text-3xl font-bold text-green-400 mb-1">100%</div>
                <div className="text-sm text-gray-400">Cotton Quality</div>
              </div>
              <div className="glass-card text-center py-4" data-testid="stat-rating">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <span className="text-3xl font-bold text-yellow-400">5.0</span>
                </div>
                <div className="text-sm text-gray-400">Customer Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Grid */}
      <section className="relative z-10 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h2 className="text-4xl font-bold mb-2">Featured Collection</h2>
              <p className="text-gray-400">Cosmic cats doing extraordinary things in space</p>
            </div>
            <button 
              onClick={() => navigate('/shop')}
              className="btn-secondary flex items-center gap-2"
              data-testid="view-all-btn"
            >
              View All
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-products">
            {featuredProducts.map((product) => (
              <div 
                key={product.id}
                className="glass-card group cursor-pointer transform transition-all duration-300 hover:scale-105"
                onClick={() => navigate(`/product/${product.id}`)}
                data-testid={`featured-${product.id}`}
              >
                <div className="relative overflow-hidden rounded-xl mb-4 bg-white p-3">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-64 object-contain group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                    New
                  </div>
                </div>
                
                <h3 className="text-lg font-bold mb-2 line-clamp-1">{product.name}</h3>
                <p className="text-gray-400 text-sm mb-3 line-clamp-2">{product.description}</p>
                
                <div className="flex justify-between items-center">
                  <div className="text-xl font-bold text-purple-400">₹{product.price_inr}</div>
                  <button 
                    className="text-sm px-4 py-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/product/${product.id}`);
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button 
              onClick={() => navigate('/shop')}
              className="btn-primary px-8 py-3"
              data-testid="see-more-btn"
            >
              See All {products.length} Designs
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-16 px-4 bg-black/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Cosmic Cat?</h2>
            <p className="text-gray-400 text-lg">Premium quality meets cosmic creativity</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card text-center group" data-testid="feature-ai">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">AI-Generated Art</h3>
              <p className="text-gray-400">Each design created using cutting-edge AI technology for unique cosmic aesthetics</p>
            </div>

            <div className="glass-card text-center group" data-testid="feature-quality">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-pink-500 to-purple-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Premium Quality</h3>
              <p className="text-gray-400">100% cotton fabric for ultimate comfort and durability that lasts</p>
            </div>

            <div className="glass-card text-center group" data-testid="feature-exclusive">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Limited Edition</h3>
              <p className="text-gray-400">Exclusive designs you won't find anywhere else in the universe</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card text-center p-12" data-testid="cta-section">
            <h2 className="text-4xl font-bold mb-4">Ready to Join the Cosmic Club?</h2>
            <p className="text-gray-300 text-lg mb-8">15 unique designs waiting for you. Premium quality, cosmic style.</p>
            <button 
              onClick={() => navigate('/shop')}
              className="btn-primary text-lg px-8 py-4"
              data-testid="cta-shop-btn"
            >
              Start Shopping Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Cosmic Cat</h3>
              <p className="text-gray-400 text-sm">Premium AI-generated t-shirt designs featuring cosmic cats. Wear the universe.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/shop" className="block text-gray-400 hover:text-white text-sm">Shop</Link>
                <Link to="/account" className="block text-gray-400 hover:text-white text-sm">My Account</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-gray-400 text-sm">cosmiccat.in</p>
              <p className="text-gray-400 text-sm">Premium cosmic apparel</p>
            </div>
          </div>
          <div className="text-center text-gray-400 text-sm pt-8 border-t border-gray-800">
            <p>© 2025 Cosmic Cat. All rights reserved. | Made with Emergent</p>
          </div>
        </div>
      </footer>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

export default Home;
